import { inject, Injector, Service } from '@angular/core';
import type {
  OnyxDropdownOptionsSourceResult,
  OnyxHttpErrorResponse,
  OnyxOption,
  OnyxPaginated,
  OnyxPagination,
} from '@onyx/angular';
import { OnyxPhonePipe } from '@onyx/angular';
import type { Observable } from 'rxjs';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  from,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { VALIDATION_ERROR_CODES } from '../../../../../common/constants/common/validation-error-codes';
import { ConflictHelper } from '../../../../../common/helpers/conflict.helper';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import type { ConflictField } from '../../../../../common/interfaces/utilities/conflict-field';
import { ApiService } from '../../../../../common/services/api.service';
import type { ImportProgress } from '../../../../common/interfaces/import-progress';
import type { PointsOfInterestImportData } from '../constants/points-of-interest-import-schema';
import type { PointOfInterestType } from '../enums/point-of-interest-type';
import type { PointOfInterestFormDto } from '../helpers/point-of-interest-form.helper';
import type { PointOfInterest } from '../interfaces/point-of-interest';

export interface PointsOfInterestFilters {
  type: PointOfInterestType | 'all';
  query?: string;
}

@Service()
export class PointsOfInterestService extends ApiService {
  private readonly phonePipe = inject(OnyxPhonePipe);
  private readonly conflictHelper = inject(ConflictHelper);
  private readonly injector = inject(Injector);

  private _reload$ = new Subject<void>();
  public get reload$() {
    return this._reload$.asObservable();
  }

  public listPointsOfInterest(
    params: OnyxPagination & PointsOfInterestFilters,
  ): Observable<OnyxPaginated<PointOfInterest>> {
    return this.get('/point-of-interest', {
      params: {
        page: params.page,
        limit: params.limit,
        ...(params.type !== 'all' && { type: params.type }),
        ...(params.query && { query: params.query }),
      },
    });
  }

  public searchPointsOfInterest(
    query: string,
    params: Omit<OnyxPagination & PointsOfInterestFilters, 'query'>,
  ): Observable<OnyxDropdownOptionsSourceResult<PointOfInterest>> {
    return this.reload$.pipe(
      startWith(undefined),
      switchMap(() => this.listPointsOfInterest({ ...params, query })),
      map((response) => ({
        options: response.items.map(
          (pointOfInterest): OnyxOption<PointOfInterest> => ({
            name: pointOfInterest.name,
            value: pointOfInterest,
            leftFlag: pointOfInterest.address.countryCode,
          }),
        ),
        totalItems: response.totalItems,
      })),
    );
  }

  public getPointOfInterest(uuid: string): Observable<PointOfInterest> {
    return this.get(`/point-of-interest/${uuid}`);
  }

  public addPointOfInterest(
    dto: PointOfInterestFormDto,
  ): Observable<Pick<PointOfInterest, 'uuid'>> {
    return this.post<Pick<PointOfInterest, 'uuid'>>(
      '/point-of-interest',
      dto,
    ).pipe(
      catchError((response) => this.handleConflictErrors(dto, response)),
      tap(() => this._reload$.next()),
    );
  }

  public importPointsOfInterest(
    data: PointsOfInterestImportData[],
  ): Observable<ImportProgress> {
    const dto = data.map((item): PointOfInterestFormDto => ({
      name: item.name,
      address: item.address,
      businessHours: item.businessHours,
      time: { averageServiceTime: item.averageServiceTime },
      phone: item.phone,
      note: { content: item.note },
      contractors: item.assignedContractors.map(({ contractor, gate }) => ({
        uuid: contractor.uuid,
        gate,
      })),
    }));

    return from(dto).pipe(
      concatMap((dto, index) =>
        this.addPointOfInterest(dto).pipe(
          map(() => ({ ok: true, index })),
          defaultIfEmpty({ ok: false, index }),
          catchError((response) => {
            ValidationHelper.handleUnexpectedResponse(response, this.injector);
            return of({ ok: false, index });
          }),
        ),
      ),
    );
  }

  public editPointOfInterest(
    uuid: string,
    dto: PointOfInterestFormDto,
  ): Observable<PointOfInterest> {
    return this.put<PointOfInterest>(`/point-of-interest/${uuid}`, dto).pipe(
      catchError((response) => this.handleConflictErrors(dto, response)),
      tap(() => this._reload$.next()),
    );
  }

  public deletePointOfInterest(uuid: string): Observable<void> {
    return this.delete<void>(`/point-of-interest/${uuid}`).pipe(
      tap(() => this._reload$.next()),
    );
  }

  private handleConflictErrors(
    dto: PointOfInterestFormDto,
    response: OnyxHttpErrorResponse,
  ): Observable<never> {
    if (!VALIDATION_ERROR_CODES.includes(response.status)) throw response;

    const conflictFields: ConflictField[] = [
      {
        key: 'name',
        message: 'pointsOfInterest.uniqueErrors.name',
        params: { name: dto.name },
      },
      {
        key: 'phone',
        message: 'pointsOfInterest.uniqueErrors.phone',
        params: { phone: this.phonePipe.transform(dto.phone!) },
      },
    ];

    this.conflictHelper.handleConflictErrors(response, conflictFields);

    throw response;
  }
}
